"use client";
import classnames from "classnames";
import { Header, MenuItem, MenuSection } from "react-aria-components";
import { Text } from "@zapier/design-system";
import type {
  GuidepostEntry,
  GuidepostCta,
  GlobalSearchEmitFunction,
} from "../types";
import { GlobalSearchEvent } from "../types";
import styles from "./SearchDropdown.module.css";

interface GuidepostProps {
  guidepost: GuidepostEntry;
  query: string;
  emit?: GlobalSearchEmitFunction;
}

export function Guidepost({ guidepost, query, emit }: GuidepostProps) {
  const { primaryCta, secondaryCta, tertiaryCta } = guidepost;

  const isLink = Boolean(
    primaryCta?.destinationUrl &&
    !primaryCta?.text &&
    !secondaryCta?.text &&
    !tertiaryCta?.text,
  );

  const getCTAhref = (cta: GuidepostCta): string | undefined => {
    // Porter doesn't expose a stable `ctaType` field, so we match on the CTA
    // label to redirect Copilot prompts to the app home page. If Porter's copy
    // changes, this will gracefully fall back to `cta.destinationUrl`.
    if (cta.text === "Build with Copilot") {
      const prompt = `${guidepost.title}: ${query}`;
      return `/app/home?copilot_prompt=${encodeURIComponent(prompt)}`;
    }
    return cta.destinationUrl || undefined;
  };

  const handleCTAClick = (
    cta: GuidepostCta,
    level: "primary" | "secondary" | "tertiary",
  ) => {
    emit?.({
      event: GlobalSearchEvent.GuidepostCTAClick,
      eventAction: "click",
      text: guidepost.title,
      customData: { query, cta, level },
    });
  };

  const cardBody = (
    <>
      <div className={styles.guidepostContent}>
        <Text type="Title" color="TextDefault">
          {guidepost.title}
        </Text>
        <Text type="SmallPrint1" color="TextWeaker">
          {guidepost.description}
        </Text>
      </div>
      {guidepost.imgUrl && (
        <div className={styles.guidepostImage}>
          <img
            src={guidepost.imgUrl}
            alt=""
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
        </div>
      )}
    </>
  );

  const renderCta = (
    cta: GuidepostCta | null | undefined,
    level: "primary" | "secondary" | "tertiary",
    variant: "tertiary" | "ghost",
  ) => {
    if (!cta?.text) return;
    const href = getCTAhref(cta);
    if (!href) return;

    return (
      <MenuItem
        id={`guidepost-cta-${level}`}
        textValue={cta.text}
        href={href}
        // A `Menu` runs with `linkBehavior: "action"`, so `onAction` both
        // fires and navigates; `onPress` would fire twice per click.
        onAction={() => handleCTAClick(cta, level)}
        className={classnames(styles.guidepostCta, {
          [styles.guidepostCtaGhost]: variant === "ghost",
        })}
      >
        {cta.iconUrl && <img src={cta.iconUrl} alt="" width={16} height={16} />}
        <Text type="Body2" color="TextDefault">
          {cta.text}
        </Text>
      </MenuItem>
    );
  };

  return (
    <MenuSection id="guidepost" className={styles.guidepostSection}>
      <Header className={styles.guidepostHeader}>
        <Text
          tag="h2"
          type="MinimalPrint1"
          color="TextWeakest"
          margin="2px 0 10px 0"
        >
          Suggested
        </Text>
        {!isLink && <div className={styles.guidepost}>{cardBody}</div>}
      </Header>
      {isLink && primaryCta ? (
        <MenuItem
          id={`guidepost-${guidepost.id}`}
          textValue={guidepost.title}
          href={getCTAhref(primaryCta)}
          // See the note on `renderCta` above.
          onAction={() => handleCTAClick(primaryCta, "primary")}
          className={classnames(styles.guidepost, styles.guidepostLinkItem)}
        >
          {cardBody}
        </MenuItem>
      ) : (
        <>
          {renderCta(primaryCta, "primary", "tertiary")}
          {renderCta(secondaryCta, "secondary", "tertiary")}
          {renderCta(tertiaryCta, "tertiary", "ghost")}
        </>
      )}
    </MenuSection>
  );
}
